import { logger } from 'features/logger/logger';

import { PLACEHOLDER_PATTERN } from './redirectUrlPlaceholders';

export function interpolateRedirectUrl(
  url: string,
  params: Record<string, string | undefined>
): string | undefined {
  const hashIndex = url.indexOf('#');
  const fragment = hashIndex === -1 ? '' : url.slice(hashIndex);
  const urlWithoutFragment = hashIndex === -1 ? url : url.slice(0, hashIndex);

  const questionMarkIndex = urlWithoutFragment.indexOf('?');
  const basePart =
    questionMarkIndex === -1
      ? urlWithoutFragment
      : urlWithoutFragment.slice(0, questionMarkIndex);
  const queryString =
    questionMarkIndex === -1
      ? ''
      : urlWithoutFragment.slice(questionMarkIndex + 1);

  // Single-pass: interpolate base and detect unresolvable placeholders
  const unresolvedBase: string[] = [];
  const interpolatedBase = basePart.replace(
    PLACEHOLDER_PATTERN,
    (match, name: string) => {
      const value = params[name];
      if (value !== undefined) {
        return encodeURIComponent(value);
      }
      unresolvedBase.push(name);
      return match;
    }
  );

  if (unresolvedBase.length > 0) {
    for (const name of unresolvedBase) {
      logger.warn(
        `Redirect URL placeholder {${name}} in path has no value — skipping redirect`
      );
    }
    return undefined;
  }

  if (!queryString) {
    return `${interpolatedBase}${fragment}`;
  }

  // Single-pass per param: interpolate and detect unresolvable in one step
  const interpolatedParams: string[] = [];
  for (const param of queryString.split('&')) {
    const unresolved: string[] = [];
    const interpolated = param.replace(
      PLACEHOLDER_PATTERN,
      (match, name: string) => {
        const value = params[name];
        if (value !== undefined) {
          return encodeURIComponent(value);
        }
        unresolved.push(name);
        return match;
      }
    );

    if (unresolved.length > 0) {
      for (const name of unresolved) {
        logger.warn(
          `Redirect URL placeholder {${name}} in query parameter has no value — stripping parameter`
        );
      }
    } else {
      interpolatedParams.push(interpolated);
    }
  }

  if (interpolatedParams.length === 0) {
    return `${interpolatedBase}${fragment}`;
  }

  return `${interpolatedBase}?${interpolatedParams.join('&')}${fragment}`;
}
